// File: src/AppWithRoutes.jsx
import React, { Suspense, useCallback, useMemo, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { AuthPage } from './components/Auth';
import PricingPage from './pages/PricingPage';
import SubscriptionGuard from './components/Auth/SubscriptionGuard';
import './App.css';
import './auth-styles.css';

// Lazy load utility with memoization
const lazyLoad = (importFn) => {
  const LazyComponent = React.lazy(importFn);
  return React.memo(React.forwardRef((props, ref) => (
    <Suspense fallback={
      <div className="min-h-screen flex items-center justify-center bg-sws-light-cream">
        <div className="bg-white p-6 rounded-lg shadow-md">
          <div className="flex items-center space-x-2">
            <svg className="animate-spin h-5 w-5 text-sws-purple" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <p className="text-sws-navy">Loading...</p>
          </div>
        </div>
      </div>
    }>
      <LazyComponent {...props} ref={ref} />
    </Suspense>
  )));
};

// Only lazy load these components
const App = lazyLoad(() => import('./App'));
const TermsOfService = lazyLoad(() => import('./pages/TermsOfService'));
const PrivacyPolicy = lazyLoad(() => import('./pages/PrivacyPolicy'));
const PasswordResetHandler = lazyLoad(() => import('./components/Auth/PasswordResetHandler'));

// Create a separate component for tracking that uses useLocation
const TrackedRoutes = () => {
  const { user, loading, signOut } = useAuth();
  const location = useLocation();
  
  // Track route changes in Google Analytics
  useEffect(() => {
    if (typeof window.gtag === 'function') {
      window.gtag('config', 'G-TN7T37WFS3', {
        page_path: location.pathname + location.search,
      });
    }
  }, [location]);

  // Memoize the loading spinner component
  const loadingSpinner = useMemo(() => (
    <div className="min-h-screen flex items-center justify-center bg-sws-light-cream">
      <div className="bg-white p-6 rounded-lg shadow-md">
        <div className="flex items-center space-x-2">
          <svg className="animate-spin h-5 w-5 text-sws-purple" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="text-sws-navy">Loading your data...</p>
        </div>
      </div>
    </div>
  ), []);

  // Memoize signOut callback
  const memoizedSignOut = useCallback(() => {
    signOut();
  }, [signOut]);

  // Memoize route elements that depend on user state
  const loginElement = useMemo(() => (
    user ? <Navigate to="/dashboard" replace /> : <AuthPage />
  ), [user]);

  const registerElement = useMemo(() => (
    user ? <Navigate to="/dashboard" replace /> : <AuthPage defaultView="register" />
  ), [user]);

  const dashboardElement = useMemo(() => (
    user ? (
      <SubscriptionGuard signOut={memoizedSignOut}>
        <App user={user} signOut={memoizedSignOut} />
      </SubscriptionGuard>
    ) : (
      <Navigate to="/pricing" replace />
    )
  ), [user, memoizedSignOut]);

  const rootElement = useMemo(() => (
    user ? <Navigate to="/dashboard" replace /> : <Navigate to="/pricing" replace />
  ), [user]);

  if (loading) {
    return loadingSpinner;
  }

  return (
    <Routes>
      {/* Public pricing page - main entry point */}
      <Route
        path="/pricing"
        element={<PricingPage />}
      />

      {/* Login/Register pages */}
      <Route
        path="/login"
        element={loginElement}
      />
      
      {/* Direct registration route */}
      <Route
        path="/register"
        element={registerElement}
      />

      {/* Password reset handler route */}
      <Route
        path="/reset-password"
        element={<PasswordResetHandler />}
      />

      {/* Protected app routes with subscription guard */}
      <Route
        path="/dashboard/*"
        element={dashboardElement}
      />

      {/* Root redirect */}
      <Route
        path="/"
        element={rootElement}
      />

      {/* Legal pages - publicly accessible */}
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />

      {/* Catch all - redirect to pricing */}
      <Route path="*" element={<Navigate to="/pricing" replace />} />
    </Routes>
  );
};

// Main component that wraps everything with Router
const AppWithRoutes = () => {
  return (
    <Router future={{ 
      v7_startTransition: true, 
      v7_relativeSplatPath: true 
    }}>
      <TrackedRoutes />
    </Router>
  );
};

export default React.memo(AppWithRoutes);